import { formsSheetsUnsaved, mountFormsSheet } from "./forms-sheet.js";

function antiforgeryToken(): string {
  return document.querySelector<HTMLInputElement>('input[name="__RequestVerificationToken"]')?.value ?? "";
}

// Forms with data-confirm ask before submitting (delete, restore, purge).
document.addEventListener("submit", (event) => {
  const form = event.target as HTMLFormElement;
  const message = form.dataset.confirm;
  if (message && !window.confirm(message)) event.preventDefault();
});

// Task checkboxes on a rendered page save immediately as a new revision.
document.addEventListener("change", async (event) => {
  const box = event.target as HTMLInputElement;
  if (!box.matches("input.task-toggle")) return;
  const article = box.closest<HTMLElement>("article[data-task-url]");
  if (!article) return;
  box.disabled = true;
  const body = new URLSearchParams({ index: box.dataset.task!, done: String(box.checked), revision: article.dataset.revision ?? "" });
  try {
    const response = await fetch(article.dataset.taskUrl!, { method: "POST", headers: { RequestVerificationToken: antiforgeryToken() }, body });
    const result = (await response.json().catch(() => ({}))) as { revision?: string; error?: string };
    if (!response.ok) {
      window.alert(result.error ?? "The task couldn't be saved.");
      window.location.reload();
      return;
    }
    article.dataset.revision = result.revision ?? "";
  } finally {
    box.disabled = false;
  }
});

// Comments on sections: a count next to each heading that has comments, and "Comment" to start one there.
function setupSectionComments(): void {
  const article = document.querySelector<HTMLElement>("article.prose[data-comment-sections]");
  if (!article) return;
  const counts = JSON.parse(article.dataset.commentSections || "{}") as Record<string, { n: number; t: string }>;
  const form = document.querySelector<HTMLFormElement>("form#new-comment");
  const canComment = article.dataset.canComment === "true" && form !== null;
  const titles = new Map<string, string>();

  for (const heading of article.querySelectorAll<HTMLElement>("h1[id^='b-'], h2[id^='b-'], h3[id^='b-'], h4[id^='b-'], h5[id^='b-'], h6[id^='b-']")) {
    titles.set(heading.id, heading.textContent?.trim() ?? "");
    const info = counts[heading.id];
    if (!info && !canComment) continue;
    const tools = document.createElement("span");
    tools.className = "section-comments";
    if (info) {
      const link = document.createElement("a");
      link.href = `#thread-${info.t}`;
      link.textContent = info.n === 1 ? "1 comment" : `${info.n} comments`;
      tools.append(link);
    }
    if (canComment) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "link add-section-comment";
      button.textContent = "Comment";
      button.addEventListener("click", () => commentOn(heading.id));
      tools.append(button);
    }
    heading.append(tools);
  }
  if (!form) return;

  const block = form.querySelector<HTMLInputElement>("input[name='block']")!;
  const on = form.querySelector<HTMLElement>(".comment-on")!;
  const sectionLink = on.querySelector<HTMLAnchorElement>("a")!;
  const textarea = form.querySelector<HTMLTextAreaElement>("textarea")!;

  function commentOn(id: string | null): void {
    block.value = id ?? "";
    on.hidden = !id;
    if (id) {
      sectionLink.href = `#${id}`;
      sectionLink.textContent = `“${titles.get(id) || "this section"}”`;
      form!.scrollIntoView({ behavior: "smooth", block: "center" });
      textarea.focus({ preventScroll: true });
    }
  }

  form.querySelector("[data-comment-whole-page]")?.addEventListener("click", () => commentOn(null));
  // A draft returned after a problem keeps its section.
  if (block.value) commentOn(block.value);
}

setupSectionComments();

// Copy buttons: data-copy names the input whose value is copied.
document.addEventListener("click", async (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-copy]");
  if (!button) return;
  const input = document.getElementById(button.dataset.copy!) as HTMLInputElement | null;
  if (!input) return;
  input.select();
  try {
    await navigator.clipboard.writeText(input.value);
  } catch {
    document.execCommand("copy");
  }
  button.textContent = "Copied";
  window.setTimeout(() => (button.textContent = "Copy"), 2000);
});

// Page pickers (e.g. limiting an AI connection): each chosen page becomes a removable chip with a hidden input.
document.addEventListener("p9-select", (event) => {
  const picker = (event.target as HTMLElement).closest<HTMLElement>("p9-autocomplete[data-page-picker]");
  const detail = (event as CustomEvent<{ value: string; id?: string }>).detail;
  const list = picker?.closest("fieldset")?.querySelector<HTMLUListElement>("[data-chosen-pages]");
  if (!picker || !list || !detail.id) return;
  picker.querySelector("input")!.value = "";
  if (list.querySelector(`input[value="${detail.id}"]`)) return;

  const item = document.createElement("li");
  const hidden = document.createElement("input");
  hidden.type = "hidden";
  hidden.name = "roots";
  hidden.value = detail.id;
  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "link";
  remove.textContent = "Remove";
  remove.addEventListener("click", () => item.remove());
  item.append(hidden, document.createTextNode(detail.value + " "), remove);
  list.append(item);
});

// Enter in a page picker picks a page; it must never submit the form (on the consent page that would approve it).
document.addEventListener("keydown", (event) => {
  const target = event.target as HTMLElement;
  if (event.key === "Enter" && target.matches("p9-autocomplete[data-page-picker] input")) event.preventDefault();
});

// AI settings: list the models installed in the person's own Ollama (the browser asks it directly).
document.addEventListener("click", async (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-ollama-find]");
  if (!button) return;
  const form = button.closest<HTMLFormElement>("[data-ollama-settings]")!;
  const endpoint = form.querySelector<HTMLInputElement>("input[name='endpoint']")!.value.trim().replace(/\/$/, "");
  const select = form.querySelector<HTMLSelectElement>("[data-ollama-models]")!;
  const status = form.querySelector<HTMLElement>("[data-ollama-status]")!;
  status.hidden = false;
  status.textContent = "Asking Ollama…";
  try {
    const response = await fetch(`${endpoint}/api/tags`);
    if (!response.ok) throw new Error(`Ollama answered ${response.status}.`);
    const { models } = (await response.json()) as { models: { name: string }[] };
    const current = select.value;
    select.replaceChildren(...models.map((m) => new Option(m.name, m.name, false, m.name === current)));
    status.textContent = models.length ? `Found ${models.length} model(s). Pick one and save.` : "Ollama has no models yet: run e.g. “ollama pull qwen3” first.";
  } catch {
    status.textContent = `Couldn't reach Ollama at ${endpoint}. Is it running, is OLLAMA_ORIGINS set to ${location.origin}, and did you let this site connect to your computer?`;
  }
});

// "Ask in Claude / ChatGPT": copies a ready-made question (in case the app ignores the prefilled text) and opens the
// app beside the archive. The app reads the archive through its connector, on the person's own subscription.
document.addEventListener("click", async (event) => {
  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[data-ask-prompt]");
  if (!link) return;
  event.preventDefault();
  try {
    await navigator.clipboard.writeText(link.dataset.askPrompt!);
  } catch {
    // Clipboard may be unavailable; the prefilled link still works where the app supports it.
  }
  const width = Math.min(560, Math.floor(screen.availWidth / 2));
  const opened = window.open(link.href, "p9-assistant", `popup,width=${width},height=${screen.availHeight},left=${screen.availWidth - width},top=0`);
  if (!opened) window.location.href = link.href;
  link.closest("details")?.removeAttribute("open");
});

// A single page picked from suggestions (e.g. "Move to page"): its id goes into the form's hidden field named by data-pick-page,
// and, with data-pick-kind, whether it's a page or a skein into the field named by that.
document.addEventListener("p9-select", (event) => {
  const picker = (event.target as HTMLElement).closest<HTMLElement>("p9-autocomplete[data-pick-page]");
  const detail = (event as CustomEvent<{ value: string; id?: string; kind?: string }>).detail;
  const form = picker?.closest("form");
  const field = form?.querySelector<HTMLInputElement>(`input[type="hidden"][name="${picker?.dataset.pickPage}"]`);
  if (!picker || !field) return;
  field.value = detail.id ?? "";
  picker.querySelector("input")!.value = detail.value;
  const kind = picker.dataset.pickKind ? form?.querySelector<HTMLInputElement>(`input[type="hidden"][name="${picker.dataset.pickKind}"]`) : null;
  if (kind) kind.value = detail.kind === "skein" ? "skein" : "page";
});
document.addEventListener("input", (event) => {
  const input = event.target as HTMLElement;
  const picker = input.closest<HTMLElement>("p9-autocomplete[data-pick-page]");
  const field = picker?.closest("form")?.querySelector<HTMLInputElement>(`input[type="hidden"][name="${picker?.dataset.pickPage}"]`);
  if (field) field.value = "";
});

// Boxes in a page that are filled in by the browser: on loading, and again when a reader's page was refreshed after
// someone saved it (p9-live-page puts the new text in, with these boxes as placeholders again).
const boxFillers: Array<(root: ParentNode) => void> = [];
const fillBoxes = (root: ParentNode): void => boxFillers.forEach((fill) => fill(root));
document.addEventListener("p9-page-refreshed", (event) => fillBoxes((event as CustomEvent<Element>).detail));

// Boards and maps inserted in pages (:::board, :::map) come as placeholders with a checked id; their component takes over.
boxFillers.push((root) => {
  const base = document.body.dataset.base ?? "/";
  for (const el of root.querySelectorAll<HTMLElement>("[data-board]")) {
    const board = document.createElement("p9-board");
    board.dataset.url = `${base}boards/${el.dataset.board}`;
    board.dataset.base = base;
    // On pages boards are for viewing; they're worked on in the page's edit view (or switched on in the side panel).
    board.dataset.mode = "view";
    el.replaceChildren(board);
  }
  for (const el of root.querySelectorAll<HTMLElement>("[data-map]")) {
    const map = document.createElement("p9-map");
    map.dataset.url = `${base}maps/${el.dataset.map}`;
    map.dataset.base = base;
    map.dataset.mode = "view";
    // focus="self": open on this page's own marker.
    const pageId = document.querySelector<HTMLElement>("article[data-page]")?.dataset.page;
    if (el.dataset.focus === "self" && pageId) map.dataset.focusPage = pageId;
    el.replaceChildren(map);
  }
});

// Character sheets from Forms (:::form) come as placeholders with a checked id; the live sheet takes their place. On a
// page a sheet is for reading, with a way to keep it in the side panel (where those who may edit it get Forms' tabs:
// view, edit, play).
boxFillers.push((root) => {
  for (const box of root.querySelectorAll<HTMLElement>("[data-form-character]")) {
    const slot = box.querySelector<HTMLElement>(":scope > .form-embed-placeholder");
    if (!slot) continue;
    const holder = document.createElement("div");
    slot.replaceWith(holder);
    box.classList.add("form-embed-live");
    const page = box.dataset.formPage || document.querySelector<HTMLElement>("article[data-page]")?.dataset.page;
    const mode = box.dataset.formMode === "edit" ? "edit" : "view";
    void mountFormsSheet(holder, box.dataset.formCharacter!, mode, page);
    if (mode === "view") {
      const beside = document.createElement("a");
      beside.className = "form-embed-beside small";
      beside.href = `${document.body.dataset.base ?? "/"}sheets/${box.dataset.formCharacter}${page ? `?page=${page}` : ""}`;
      beside.dataset.openInPanel = "";
      beside.title = "Keep this character sheet next to the archive while you browse or play";
      beside.textContent = "⇥ Sheet in the side panel";
      box.append(beside);
    }
  }
});

// A sheet open for editing on its own page (/sheets/…): leaving with work not yet in Forms asks first.
window.addEventListener("beforeunload", (event) => {
  if (document.querySelector('[data-form-mode="edit"]') && formsSheetsUnsaved(document)) event.preventDefault();
});

// Tapestry (docs/tapestry-design.md): thread, skein and event boxes get their panel, and views their contents, from the
// server, which only puts in what this person may see.
{
  const fragments = `${document.body.dataset.base ?? "/"}tapestry/fragment`;
  const pageId = document.querySelector<HTMLElement>("article[data-page]")?.dataset.page;
  const fill = async (target: Element, query: URLSearchParams): Promise<void> => {
    try {
      const response = await fetch(`${fragments}?${query}`);
      if (!response.ok) {
        target.textContent = response.status === 404 ? "Not available to you." : "Couldn't load this part.";
        return;
      }
      const template = document.createElement("template");
      template.innerHTML = await response.text();
      target.replaceWith(template.content);
    } catch {
      target.textContent = "Couldn't load this part.";
    }
  };
  const viewQuery = (box: HTMLElement): URLSearchParams => {
    const query = new URLSearchParams({ handler: "View", view: box.dataset.tapestryView!, on: pageId! });
    for (const key of ["of", "under", "layout", "calendar", "lanes", "from", "to", "depth", "kinds", "asof", "threads", "members"] as const)
      if (box.dataset[key]) query.set(key, box.dataset[key]!);
    return query;
  };
  boxFillers.push((root) => {
    for (const box of root.querySelectorAll<HTMLElement>("[data-thread], [data-skein], [data-event]")) {
      const kind = box.dataset.thread ? "thread" : box.dataset.event ? "event" : "skein";
      const slot = box.querySelector(":scope > .form-embed-placeholder");
      if (slot) void fill(slot, new URLSearchParams({ handler: kind[0]!.toUpperCase() + kind.slice(1), id: box.dataset[kind]! }));
    }
    for (const box of root.querySelectorAll<HTMLElement>("[data-tapestry-view]")) {
      if (!pageId) continue;
      const slot = box.querySelector(":scope > .form-embed-placeholder");
      if (slot) void fill(slot, viewQuery(box));
    }
  });

  // Thread tracker: cards on pages the person can edit move to another status, dragged to a column or with their
  // Move menu (phones, keyboards). The server writes the status into the thread's box, as a version of its page.
  const move = async (card: HTMLElement, status: string): Promise<void> => {
    const url = card.dataset.statusUrl;
    if (!url) return;
    card.classList.add("tp-moving");
    try {
      const response = await fetch(url, { method: "POST", headers: { RequestVerificationToken: antiforgeryToken() }, body: new URLSearchParams({ status }) });
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) window.alert(result.error ?? "The thread couldn't be moved.");
    } catch {
      window.alert("The thread couldn't be moved.");
    }
    // A thread on this very page changed the page (its panel, its version): reload it. Otherwise the tracker is enough.
    const view = card.closest<HTMLElement>("[data-tapestry-view]");
    const current = view?.querySelector(":scope > .tapestry-box");
    if (card.dataset.host === pageId || !view || !current || !pageId) window.location.reload();
    else await fill(current, viewQuery(view));
  };
  // Events settled without the editor: "past their date" (happened, cancelled, move to a date), and "Make this Now"
  // (the chronicle's Now moves to the event's time).
  // Each is a version of the event's page; the page (or the timeline) is shown again from the server afterwards.
  const settle = async (holder: HTMLElement, body: URLSearchParams): Promise<void> => {
    holder.classList.add("tp-moving");
    try {
      const response = await fetch(holder.dataset.eventUrl!, { method: "POST", headers: { RequestVerificationToken: antiforgeryToken() }, body });
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        holder.classList.remove("tp-moving");
        return void window.alert(result.error ?? "The event couldn't be changed.");
      }
    } catch {
      holder.classList.remove("tp-moving");
      return void window.alert("The event couldn't be changed.");
    }
    const view = holder.closest<HTMLElement>("[data-tapestry-view]");
    const current = view?.querySelector(":scope > .tapestry-box");
    // A Now that moved, or an event on this very page, changes more than the timeline: show the page again.
    if (body.has("now") || holder.dataset.host === pageId || !view || !current || !pageId) window.location.reload();
    else await fill(current, viewQuery(view));
  };
  document.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-event-set]");
    const holder = button?.closest<HTMLElement>("[data-event-url]");
    if (button && holder) void settle(holder, new URLSearchParams({ [button.dataset.eventSet!]: button.dataset.value! }));
  });
  // "Set Now" on a timeline: the chronicle's Now moves (a version of the page that holds it); the page is shown again.
  document.addEventListener("submit", async (event) => {
    const form = (event.target as HTMLElement).closest<HTMLFormElement>("form[data-now-set]");
    if (!form) return;
    event.preventDefault();
    const when = new FormData(form).get("when")?.toString().trim() ?? "";
    form.classList.add("tp-moving");
    try {
      const response = await fetch(form.dataset.nowUrl!, { method: "POST", headers: { RequestVerificationToken: antiforgeryToken() }, body: new URLSearchParams({ when }) });
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) {
        form.classList.remove("tp-moving");
        return void window.alert(result.error ?? "Now couldn't be set.");
      }
      window.location.reload();
    } catch {
      form.classList.remove("tp-moving");
      window.alert("Now couldn't be set.");
    }
  });
  // The Weave's "Copy to a board" (docs/tapestry-design.md §3.9): an ordinary board of it goes on this page, right after
  // its box (which one, counted as this person sees the page); the page is shown again with it.
  document.addEventListener("click", async (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-weave-copy-button]");
    const box = button?.closest<HTMLElement>("[data-weave-copy]");
    const view = box?.closest<HTMLElement>("[data-tapestry-view]");
    if (!button || !box || !view) return;
    const index = [...document.querySelectorAll('article[data-page] [data-tapestry-view="weave"]')].indexOf(view);
    button.disabled = true;
    try {
      const response = await fetch(`${box.dataset.weaveCopy}&n=${Math.max(0, index)}`, { method: "POST", headers: { RequestVerificationToken: antiforgeryToken() } });
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(result.error ?? "The board couldn't be made.");
      window.location.reload();
    } catch (error) {
      button.disabled = false;
      window.alert(error instanceof Error ? error.message : "The board couldn't be made.");
    }
  });

  // Run sheets (docs/tapestry-design.md §3.6): another day or other lanes ask the server for the sheet again; with
  // "Move events" on, an event with an exact time of its own is dragged along its lane (or moved with the arrow keys) by
  // quarter hours, and the server writes the new time into its box (a version of its page).
  // Razor writes data-* attributes even when null: an empty one means the event can't be moved here.
  const MOVABLE = '[data-retime-url]:not([data-retime-url=""])';
  const sheetAgain = async (box: HTMLElement, change: Record<string, string> = {}, moving = false): Promise<void> => {
    const view = box.closest<HTMLElement>("[data-tapestry-view]");
    if (!view) return;
    const query = viewQuery(view);
    if (box.dataset.rsLanes) query.set("lanes", box.dataset.rsLanes);
    if (box.dataset.rsAt) query.set("at", box.dataset.rsAt);
    for (const [key, value] of Object.entries(change)) query.set(key, value);
    await fill(box, query);
    const fresh = view.querySelector<HTMLElement>(":scope > .rs-box");
    if (moving && fresh) setMoving(fresh, true);
  };
  const setMoving = (box: HTMLElement, on: boolean): void => {
    box.classList.toggle("rs-moving", on);
    box.querySelector("[data-rs-move]")?.setAttribute("aria-pressed", String(on));
    const hint = box.querySelector<HTMLElement>(".rs-move-hint");
    if (hint) hint.hidden = !on;
  };
  document.addEventListener("click", (event) => {
    const clicked = event.target as HTMLElement;
    const box = clicked.closest<HTMLElement>(".rs-box");
    if (!box) return;
    const go = clicked.closest<HTMLButtonElement>("[data-rs-go]");
    const lanes = clicked.closest<HTMLElement>("[data-rs-lanes-set]");
    if (go?.dataset.rsGo) void sheetAgain(box, { at: go.dataset.rsGo }, box.classList.contains("rs-moving"));
    else if (lanes) void sheetAgain(box, { lanes: lanes.dataset.rsLanesSet! }, box.classList.contains("rs-moving"));
    else if (clicked.closest("[data-rs-move]")) setMoving(box, !box.classList.contains("rs-moving"));
    // While moving, a click on an event is the end of a drag, not a way to its page.
    else if (box.classList.contains("rs-moving") && clicked.closest(`.rs-bar${MOVABLE}`)) event.preventDefault();
  });
  type Drag = { bar: HTMLElement; x: number; width: number; span: number; hour: number; minutes: number; by: number; pointer: number };
  let sheetDrag: Drag | null = null;
  const measure = (bar: HTMLElement): Omit<Drag, "x" | "by" | "pointer"> => {
    const grid = bar.closest<HTMLElement>(".rs-grid")!;
    return {
      bar, width: bar.closest<HTMLElement>(".rs-track")!.getBoundingClientRect().width,
      span: Number(grid.dataset.rsSpan), hour: Number(grid.dataset.rsHour), minutes: Number(grid.dataset.rsMinutes) || 60,
    };
  };
  // "+1:15": how far it moves, in the sheet's hours and minutes.
  const shown = (by: number, hour: number, minutes: number): string => {
    const total = Math.round((Math.abs(by) / hour) * minutes);
    return `${by < 0 ? "−" : "+"}${Math.floor(total / minutes)}:${String(total % minutes).padStart(2, "0")}`;
  };
  const shift = (d: Pick<Drag, "bar" | "width" | "span" | "hour" | "minutes">, by: number): void => {
    d.bar.style.transform = by ? `translateX(${(by / d.span) * d.width}px)` : "";
    if (by) d.bar.dataset.rsShift = shown(by, d.hour, d.minutes);
    else delete d.bar.dataset.rsShift;
  };
  const retime = async (bar: HTMLElement, by: number): Promise<void> => {
    const box = bar.closest<HTMLElement>(".rs-box");
    bar.classList.add("tp-moving");
    try {
      const response = await fetch(bar.dataset.retimeUrl!, {
        method: "POST", headers: { RequestVerificationToken: antiforgeryToken() }, body: new URLSearchParams({ by: String(Math.round(by)) }),
      });
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) throw new Error(result.error ?? "The event couldn't be moved.");
    } catch (error) {
      bar.classList.remove("tp-moving");
      shift(measure(bar), 0);
      return void window.alert(error instanceof Error ? error.message : "The event couldn't be moved.");
    }
    // An event on this very page changed the page: show it again. Otherwise the sheet is enough.
    if (bar.dataset.host === pageId || !box) window.location.reload();
    else await sheetAgain(box, {}, true);
  };
  document.addEventListener("pointerdown", (event) => {
    const bar = (event.target as HTMLElement).closest<HTMLElement>(`.rs-moving .rs-bar${MOVABLE}`);
    if (!bar || event.button !== 0 || sheetDrag) return;
    event.preventDefault();
    sheetDrag = { ...measure(bar), x: event.clientX, by: 0, pointer: event.pointerId };
    bar.setPointerCapture(event.pointerId);
    bar.classList.add("rs-dragging");
  });
  document.addEventListener("pointermove", (event) => {
    if (!sheetDrag || event.pointerId !== sheetDrag.pointer) return;
    const step = sheetDrag.hour / 4;
    sheetDrag.by = Math.round(((event.clientX - sheetDrag.x) / sheetDrag.width) * sheetDrag.span / step) * step;
    shift(sheetDrag, sheetDrag.by);
  });
  const drop = (event: PointerEvent) => {
    if (!sheetDrag || event.pointerId !== sheetDrag.pointer) return;
    const { bar, by } = sheetDrag;
    sheetDrag = null;
    bar.classList.remove("rs-dragging");
    if (by && event.type === "pointerup") void retime(bar, by);
    else shift(measure(bar), 0);
  };
  document.addEventListener("pointerup", drop);
  document.addEventListener("pointercancel", drop);
  // Arrow keys: a quarter hour each, saved once they stop for a moment.
  const pending = new Map<HTMLElement, { by: number; timer: number }>();
  document.addEventListener("keydown", (event) => {
    const bar = (event.target as HTMLElement).closest?.<HTMLElement>(`.rs-moving .rs-bar${MOVABLE}`);
    if (!bar || (event.key !== "ArrowLeft" && event.key !== "ArrowRight")) return;
    event.preventDefault();
    const d = measure(bar);
    const was = pending.get(bar);
    if (was) window.clearTimeout(was.timer);
    const by = (was?.by ?? 0) + (event.key === "ArrowLeft" ? -1 : 1) * (d.hour / 4);
    shift(d, by);
    pending.set(bar, {
      by, timer: window.setTimeout(() => {
        pending.delete(bar);
        if (by) void retime(bar, by);
      }, 900),
    });
  });
  document.addEventListener("submit", (event) => {
    const form = (event.target as HTMLElement).closest<HTMLFormElement>("form[data-event-move]");
    const holder = form?.closest<HTMLElement>("[data-event-url]");
    if (!form || !holder) return;
    event.preventDefault();
    // A new date (an event's when) or a new deadline (a thread's or stitch's by).
    const body = new URLSearchParams();
    for (const [key, value] of new FormData(form)) if (value.toString().trim()) body.set(key, value.toString().trim());
    if ([...body.keys()].length) void settle(holder, body);
  });
  // Revealing a thread, skein or event, or hiding it again (the GMs): a version of its page; the page is shown again.
  document.addEventListener("click", async (event) => {
    const button = (event.target as HTMLElement).closest?.<HTMLButtonElement>("button[data-reveal-url]");
    if (!button || button.disabled) return;
    button.disabled = true;
    try {
      const response = await fetch(button.dataset.revealUrl!, {
        method: "POST", headers: { RequestVerificationToken: antiforgeryToken() }, body: new URLSearchParams({ revealed: button.dataset.revealed ?? "true" }),
      });
      const result = (await response.json().catch(() => ({}))) as { error?: string };
      if (!response.ok) window.alert(result.error || "That couldn't be changed.");
      else return void window.location.reload();
    } catch {
      window.alert("That couldn't be changed.");
    }
    button.disabled = false;
  });
  document.addEventListener("click", (event) => {
    const choice = (event.target as HTMLElement).closest<HTMLElement>("[data-move-to]");
    const card = choice?.closest<HTMLElement>(".tp-card[data-status-url]");
    if (choice && card) void move(card, choice.dataset.moveTo!);
  });
  let dragged: HTMLElement | null = null;
  const target = (event: DragEvent): HTMLElement | null => {
    const column = (event.target as HTMLElement).closest?.<HTMLElement>(".tp-column[data-status]") ?? null;
    return dragged && column && !column.contains(dragged) && column.closest(".tp-board") === dragged.closest(".tp-board") ? column : null;
  };
  const unmark = () => document.querySelectorAll(".tp-drop").forEach((c) => c.classList.remove("tp-drop"));
  document.addEventListener("dragstart", (event) => {
    const card = (event.target as HTMLElement).closest?.<HTMLElement>(".tp-card[data-status-url]");
    if (!card) return;
    dragged = card;
    event.dataTransfer?.setData("text/plain", card.querySelector(".tp-title")?.textContent ?? "");
    if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
    card.classList.add("tp-dragging");
  });
  document.addEventListener("dragend", () => {
    dragged?.classList.remove("tp-dragging");
    dragged = null;
    unmark();
  });
  document.addEventListener("dragover", (event) => {
    const column = target(event);
    if (!column) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
    if (!column.classList.contains("tp-drop")) {
      unmark();
      column.classList.add("tp-drop");
    }
  });
  document.addEventListener("drop", (event) => {
    const column = target(event);
    if (!column || !dragged) return;
    event.preventDefault();
    const card = dragged;
    unmark();
    column.append(card); // at once, so it feels moved; the tracker is reloaded from the server right after
    void move(card, column.dataset.status!);
  });
}

fillBoxes(document);

// "Beside the archive" links: open their address in the workspace's side panel.
document.addEventListener("click", (event) => {
  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[data-open-in-panel]");
  if (!link) return;
  event.preventDefault();
  void import("./p9-workspace.js").then((m) => m.openInPanel(link.getAttribute("href")!));
});

// A board just inserted while editing: show it, editable, with the page's other boards.
document.addEventListener("p9-board-created", (event) => {
  const section = document.querySelector<HTMLElement>("[data-boards-section]");
  const id = (event as CustomEvent<{ id: string }>).detail.id;
  if (!section) return;
  const base = document.body.dataset.base ?? "/";
  const entry = document.createElement("div");
  entry.className = "edit-board";
  entry.id = `board-${id}`;
  entry.dataset.boardEntry = "";
  const board = document.createElement("p9-board");
  board.dataset.url = `${base}boards/${id}`;
  board.dataset.base = base;
  board.dataset.mode = "edit";
  entry.append(board);
  if (!section.querySelector("h2")) {
    const h = document.createElement("h2");
    h.textContent = "Boards on this page";
    section.prepend(h);
  }
  section.append(entry);
  entry.scrollIntoView({ behavior: "smooth", block: "start" });
});

// A map just inserted while editing: the same, under "Maps on this page".
document.addEventListener("p9-map-created", (event) => {
  const section = document.querySelector<HTMLElement>("[data-maps-section]");
  const id = (event as CustomEvent<{ id: string }>).detail.id;
  if (!section) return;
  const base = document.body.dataset.base ?? "/";
  const entry = document.createElement("div");
  entry.className = "edit-board";
  entry.id = `map-${id}`;
  entry.dataset.mapEntry = "";
  const map = document.createElement("p9-map");
  map.dataset.url = `${base}maps/${id}`;
  map.dataset.base = base;
  map.dataset.mode = "edit";
  entry.append(map);
  if (!section.querySelector("h2")) {
    const h = document.createElement("h2");
    h.textContent = "Maps on this page";
    section.prepend(h);
  }
  section.append(entry);
  entry.scrollIntoView({ behavior: "smooth", block: "start" });
});

// Dropdown menus (<details class="dropdown">): one open at a time; a click elsewhere or Escape closes them.
const closeDropdowns = (except?: Element | null) => {
  for (const open of document.querySelectorAll<HTMLDetailsElement>("details.dropdown[open]")) if (open !== except) open.open = false;
};
document.addEventListener("click", (event) => {
  const target = event.target as Element;
  const inside = target.closest("details.dropdown");
  // Choosing an entry closes its menu too.
  closeDropdowns(inside && !target.closest('[role="menuitem"]') ? inside : null);
});
// Menus open toward the left (under their button's right edge); one that would run off the left edge opens rightward.
// The edge is that of the page's text area when the menu is in it: it scrolls, so it cuts off what sticks out.
document.addEventListener("toggle", (event) => {
  const menu = event.target as HTMLDetailsElement;
  if (!menu.matches?.("details.dropdown") || !menu.open) return;
  const list = menu.querySelector<HTMLElement>(".dropdown-list");
  if (!list) return;
  list.classList.remove("opens-right");
  const edge = menu.closest(".page-body")?.getBoundingClientRect().left ?? 0;
  if (list.getBoundingClientRect().left < edge + 8) list.classList.add("opens-right");
}, true);
// A click into the workspace's frames doesn't reach this document, but takes the focus away from it.
window.addEventListener("blur", () => closeDropdowns());
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;
  const open = document.querySelector<HTMLDetailsElement>("details.dropdown[open]");
  if (!open) return;
  open.open = false;
  open.querySelector("summary")?.focus();
});

// New page: the who-will-see chip says what the choice inside it means; picking a group picks "these groups";
// "what they may do" only matters when the page gets grants of its own.
for (const chip of document.querySelectorAll<HTMLDetailsElement>("[data-new-sharing]")) {
  const line = chip.querySelector<HTMLElement>("[data-share-text]");
  const levels = chip.querySelector<HTMLElement>("[data-share-level]");
  const update = () => {
    const choice = chip.querySelector<HTMLInputElement>('input[name="Share"]:checked');
    const groups = [...chip.querySelectorAll<HTMLInputElement>('input[name="ShareGroups"]:checked')].map((g) => "#" + g.dataset.slug);
    let text = choice?.dataset.text ?? "Only you can see this page";
    if (choice?.value === "groups") text = groups.length > 0 ? `Visible to ${groups.join(", ")} and you` : "Only you can see this page";
    if (line) line.textContent = text;
    chip.classList.toggle("audience-limited", choice?.dataset.limited !== "false");
    if (levels) levels.hidden = !(choice?.value === "all" || (choice?.value === "groups" && groups.length > 0));
  };
  chip.addEventListener("change", (event) => {
    const target = event.target as HTMLInputElement;
    if (target.name === "ShareGroups" && target.checked) {
      const groupsChoice = chip.querySelector<HTMLInputElement>('input[name="Share"][value="groups"]');
      if (groupsChoice) groupsChoice.checked = true;
    }
    update();
  });
}

// The header's height, for things that stick below it (the page tree, the editor toolbar).
const siteHeader = document.querySelector<HTMLElement>("body > .site-header");
if (siteHeader)
  new ResizeObserver(() => document.documentElement.style.setProperty("--header-h", `${siteHeader.offsetHeight}px`)).observe(siteHeader);

// More → keep the page head in view, or scroll the whole page. Remembered in this browser.
const pinHead = document.querySelector<HTMLButtonElement>("button[data-pin-head]");
if (pinHead) {
  const label = () => {
    pinHead.textContent = document.documentElement.classList.contains("page-head-scrolls")
      ? "Keep the title and buttons in view"
      : "Scroll the whole page";
  };
  label();
  pinHead.hidden = false;
  pinHead.addEventListener("click", () => {
    const scrolls = document.documentElement.classList.toggle("page-head-scrolls");
    try {
      if (scrolls) localStorage.setItem("p9.pageHead", "scroll");
      else localStorage.removeItem("p9.pageHead");
    } catch {
      /* only for this visit */
    }
    label();
  });
}

// In the workspace's panel, links open in the main area (the <base> set in _Layout); the AI chat's own pages stay in the panel.
if (document.documentElement.classList.contains("frame-panel")) {
  const ai = new URL(`${document.body.dataset.base ?? "/"}ai`, location.href).pathname;
  const staysInPanel = (url: string) => {
    const path = new URL(url, location.href).pathname;
    return path === ai || path.startsWith(`${ai}/`);
  };
  document.addEventListener("click", (event) => {
    const link = (event.target as Element).closest<HTMLAnchorElement>("a[href]");
    if (link && !link.hasAttribute("target") && staysInPanel(link.href)) link.target = "_self";
  }, true);
  document.addEventListener("submit", (event) => {
    const form = event.target as HTMLFormElement;
    if (!form.hasAttribute("target") && staysInPanel(form.action)) form.target = "_self";
  }, true);
}
